import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Paper, Button, Box, IconButton, Typography, CircularProgress } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight, FiberManualRecord } from '@mui/icons-material';

// Main App component which renders the ImageCarousel
function ImageVideoCarousel({products}) {
  const [activeStep, setActiveStep] = useState(0); // State to manage the currently active slide index
  const autoPlayIntervalRef = useRef(null); // Ref to hold the interval ID for autoplay
  const isHovered = useRef(false); // Ref to track if the carousel is hovered
  const [generatedDescription, setGeneratedDescription] = useState(''); // State for LLM-generated description
  const [isLoading, setIsLoading] = useState(false); // State for loading indicator

  // State for swipe functionality
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const carouselRef = useRef(null); // Ref to the carousel container for event listeners

  const totalSlides = products.length; // Total number of slides

  // Function to navigate to the next slide, wrapped in useCallback for dependencies
  const handleNext = useCallback(() => {
    setActiveStep((prevIndex) => (prevIndex + 1) % totalSlides);
    setGeneratedDescription(''); // Clear generated description on slide change
  }, [totalSlides]);

  // Function to navigate to the previous slide, wrapped in useCallback for dependencies
  const handleBack = useCallback(() => {
    setActiveStep((prevIndex) => (prevIndex - 1 + totalSlides) % totalSlides);
    setGeneratedDescription(''); // Clear generated description on slide change
  }, [totalSlides]);

  // Function to directly jump to a slide
  const handleDotClick = (index) => {
    setActiveStep(index);
    setGeneratedDescription(''); // Clear generated description on slide change
  };

  // Effect for autoplay functionality
  useEffect(() => {
    const startAutoPlay = () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
      }
      autoPlayIntervalRef.current = setInterval(() => {
        if (!isHovered.current && !isLoading) {
          handleNext();
        }
      }, 7000); // Autoplay interval: 5 seconds
    };

    startAutoPlay();

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
      }
    };
  }, [totalSlides, handleNext, isLoading]);

  // Event handlers for mouse enter and leave to control autoplay on hover
  const handleMouseEnter = () => {
    isHovered.current = true;
  };

  const handleMouseLeave = () => {
    isHovered.current = false;
  };

  // ✨ Gemini API Integration: Function to generate description
  const generateNewDescription = async () => {
    setIsLoading(true);
    const currentItemName = products[activeStep].name;
    const prompt = `Generate a concise and creative description (around 20-30 words) for an image that has the title: '${currentItemName}'. Make it engaging and suitable for a carousel.`;

    let chatHistory = [];
    chatHistory.push({ role: "user", parts: [{ text: prompt }] });
    const payload = { contents: chatHistory };
    const apiKey = ""; // Canvas will automatically provide this at runtime
    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-05-20:generateContent?key=${apiKey}`;

    try {
      let response;
      let delay = 1000;
      for (let i = 0; i < 3; i++) {
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          break;
        }
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        const text = result.candidates[0].content.parts[0].text;
        setGeneratedDescription(text);
      } else {
        console.error("Gemini API returned an unexpected structure or no content.");
        setGeneratedDescription("Could not generate a new description. Please try again.");
      }
    } catch (error) {
      console.error("Error calling Gemini API:", error);
      setGeneratedDescription("Failed to generate description due to an error.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Swipe Functionality ---
  const handleTouchStart = (e) => {
    setTouchStartX(e.targetTouches[0].clientX);
    setTouchEndX(null); // Reset EndX on new touch start
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return; // No valid swipe

    const minSwipeDistance = 50; // Minimum distance for a swipe to be recognized
    const distance = touchStartX - touchEndX;

    if (distance > minSwipeDistance) {
      // Swiped left
      handleNext();
    } else if (distance < -minSwipeDistance) {
      // Swiped right
      handleBack();
    }

    // Reset touch coordinates
    setTouchStartX(null);
    setTouchEndX(null);
  };

  // Mouse drag functionality for desktop
  const [mouseDownX, setMouseDownX] = useState(null);
  const [mouseIsDown, setMouseIsDown] = useState(false);

  const handleMouseDown = (e) => {
    e.preventDefault(); // Prevent native image dragging etc. within the carousel
    setMouseDownX(e.clientX);
    setMouseIsDown(true);
  };

  const handleMouseMove = (e) => {
    if (!mouseIsDown) return;
    e.preventDefault(); // Prevent default browser drag behavior
    setTouchEndX(e.clientX); // Using touchEndX to store current mouse position
  };

  const handleMouseUp = () => {
    if (!mouseIsDown) return;

    setMouseIsDown(false);
    if (mouseDownX === null || touchEndX === null) return;

    const minDragDistance = 50;
    const distance = mouseDownX - touchEndX;

    if (distance > minDragDistance) {
      handleNext();
    } else if (distance < -minDragDistance) {
      handleBack();
    }

    setMouseDownX(null);
    setTouchEndX(null);
  };

  // Add event listeners for mouse drag on component mount
  useEffect(() => {
    const carouselElement = carouselRef.current;
    if (carouselElement) {
      carouselElement.addEventListener('mousedown', handleMouseDown);
      carouselElement.addEventListener('mousemove', handleMouseMove);
      carouselElement.addEventListener('mouseup', handleMouseUp);
      carouselElement.addEventListener('mouseleave', handleMouseUp); // End drag if mouse leaves element

      return () => {
        carouselElement.removeEventListener('mousedown', handleMouseDown);
        carouselElement.removeEventListener('mousemove', handleMouseMove);
        carouselElement.removeEventListener('mouseup', handleMouseUp);
        carouselElement.removeEventListener('mouseleave', handleMouseUp);
      };
    }
  }, [handleMouseDown, handleMouseMove, handleMouseUp]);


  return (
    <div
    ref={carouselRef} // Attach ref for event listeners
    onMouseEnter={handleMouseEnter} // Pause autoplay on mouse enter
    onMouseLeave={handleMouseLeave} // Resume autoplay on mouse leave
    onTouchStart={handleTouchStart} // Touch swipe start
    onTouchMove={handleTouchMove}   // Touch swipe move
    onTouchEnd={handleTouchEnd}     // Touch swipe end
    >
    {/* Carousel Content Area */}
        {products[activeStep].type === 'video' ? (
        <video
            src={require(process.env.PUBLIC_URL+products[activeStep].mediaSrc)}
            alt={products[activeStep].name}
            style={{width:'90%', height: '40vw', objectFit:"fill"}}
            muted    // Mute by default for autoplay (most browsers require this)
            autoPlay // Autoplay the video
        >
            Your browser does not support the video tag.
        </video>
        ) : (
        <img
            src={require(process.env.PUBLIC_URL+products[activeStep].mediaSrc)}
            alt={products[activeStep].name}
            style={{ maxHeight: '350px', width: '100%' }}
            onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/800x400/DC3545/FFFFFF?text=Image+Load+Error"; }}
        />
        )}

    {/* Custom Clickable Dot Indicators */}
    <Box className="flex justify-center mt-4 pb-2 bg-white rounded-b-xl">
        {products.map((_, idx) => (
        <IconButton
            key={idx}
            onClick={() => handleDotClick(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            className={`mx-1 p-1 transition-all duration-300 ${
            idx === activeStep ? 'text-blue-600 scale-125' : 'text-gray-400 hover:text-gray-600'
            }`}
        >
            <FiberManualRecord fontSize="small" />
        </IconButton>
        ))}
    </Box>
    </div>
  );
}

export default ImageVideoCarousel;
